import React from 'react';
import { accentFor, cn } from '../ui';

interface SvgCourseThumbnailProps {
  title: string;
  width: number;
  height: number;
  seed?: string | number;
}

/**
 * Fallback cover for courses without an uploaded image. It is a typographic mark,
 * not an illustration: the course initials on a plain surface, with the accent
 * chosen deterministically from the seed so the same course always looks the same.
 *
 * The previous version ran a 100ms `setInterval` per thumbnail to drive a breathing
 * gradient, which meant a grid of twelve cards re-rendered 120 times a second. It
 * is now a static, purely presentational SVG.
 *
 * The colour comes from the shared category accents, so a course with no cover
 * image still sits in its subject's colour rather than in flat grey — and it
 * matches the chips on the same card.
 */

const simpleHash = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
};

const SvgCourseThumbnail: React.FC<SvgCourseThumbnailProps> = ({
  title,
  width,
  height,
  seed,
}) => {
  const currentSeed =
    typeof seed === 'number'
      ? Math.abs(seed)
      : typeof seed === 'string'
      ? simpleHash(seed)
      : simpleHash(title);

  // Seeded by the course id, so a given course always draws the same mark.
  const palette = accentFor(String(currentSeed));

  const words = title.trim().split(/\s+/).filter(Boolean);
  let initials = "";
  if (words.length > 0) {
    initials += words[0][0];
    if (words.length > 1 && words[1].length > 0) {
      initials += words[1][0];
    } else if (words.length === 1 && words[0].length > 1) {
      initials = words[0].substring(0, 2);
    }
  } else if (title.length > 0) {
    initials = title.substring(0, Math.min(title.length, 2));
  }
  initials = initials.toUpperCase();
  if (initials.length === 0) {
    initials = "??";
  }

  const textX = width / 2;
  const textY = height / 2;
  const baseFontSize = height * 0.32;
  const fontSize = initials.length === 1 ? baseFontSize : baseFontSize * 0.9;

  return (
    // The box takes the ratio from the width/height props rather than a fixed pixel
    // size, so the mark fills whatever cover slot it is dropped into.
    <div
      className={cn('relative select-none', palette.soft, palette.text)}
      style={{ width: '100%', aspectRatio: `${width} / ${height}` }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label={`Course thumbnail for ${title}`}
        className="h-full w-full"
      >
        {[0.18, 0.34, 0.5, 0.66].map((x, index) => {
          const barHeight = height * (0.16 + index * 0.07);
          const barWidth = width * 0.045;
          return (
            <rect
              key={x}
              x={width * x}
              y={height - barHeight - height * 0.1}
              width={barWidth}
              height={barHeight}
              rx={barWidth * 0.3}
              fill="currentColor"
              opacity="0.14"
            />
          );
        })}
        <text
          x={textX}
          y={textY}
          fontSize={fontSize}
          fontFamily="Fraunces, Georgia, serif"
          fontWeight="600"
          fill="currentColor"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ letterSpacing: '0.06em' }}
        >
          {initials}
        </text>
        <line
          x1={textX - fontSize * 0.42}
          y1={textY + fontSize * 0.62}
          x2={textX + fontSize * 0.42}
          y2={textY + fontSize * 0.62}
          stroke="currentColor"
          strokeWidth="1.5"
          opacity="0.4"
        />
      </svg>
    </div>
  );
};


export default SvgCourseThumbnail;
